import type { CreativesSurface } from '@alison/analytics';
import { AnalyticsService } from '@alison/analytics/client';
import { shadows } from '@alison/ui';
import ArrowDownwardOutlined from '@mui/icons-material/ArrowDownwardOutlined';
import ArrowDropDown from '@mui/icons-material/ArrowDropDown';
import ArrowDropUp from '@mui/icons-material/ArrowDropUp';
import ArrowRight from '@mui/icons-material/ArrowRight';
import ArrowUpwardOutlined from '@mui/icons-material/ArrowUpwardOutlined';
import CloseOutlined from '@mui/icons-material/CloseOutlined';
import DragIndicatorOutlined from '@mui/icons-material/DragIndicatorOutlined';
import ExpandMoreOutlined from '@mui/icons-material/ExpandMoreOutlined';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import MenuItem from '@mui/material/MenuItem';
import Popover from '@mui/material/Popover';
import Select from '@mui/material/Select';
import Typography from '@mui/material/Typography';
import { type DragEvent, type MouseEvent, type ReactNode, useMemo, useState } from 'react';
import { FilterCheckbox } from '../filters/FilterCheckbox.js';
import { MenuSearchInput } from '../filters/MenuSearchInput.js';
import type { MetricCatalog } from './data/api.js';
import {
  CREATIVE_ID_SORT_FIELD,
  categoryLabel,
  clearAllButSort,
  type DisplayedMetrics as Displayed,
  groupMetrics,
  MIN_DISPLAYED_METRICS,
  moveMetric,
  preferredSortMetric,
  removeMetric,
  selectableNames,
  setSortField,
  setSortMetric,
  toggleMetric,
} from './data/displayedMetrics.js';
import { ELLIPSIS_SX as BASE_ELLIPSIS_SX } from './utils.js';

const SECTION_SX = { py: 2, px: 2 };

const FOOTER_BUTTON_SX = { height: 28, px: 3 };

const ELLIPSIS_SX = { ...BASE_ELLIPSIS_SX, flex: 1, minWidth: 0 };

const LINK_BUTTON_SX = {
  ml: 'auto',
  border: 0,
  p: 0,
  bgcolor: 'transparent',
  color: 'primary.main',
  cursor: 'pointer',
  '&:hover': { textDecoration: 'underline' },
};

const COUNT_BADGE_SX = {
  display: 'inline-grid',
  placeItems: 'center',
  flexShrink: 0,
  minWidth: 18,
  height: 16,
  borderRadius: 1.6,
  bgcolor: 'primary.main',
  color: 'primary.contrastText',
  fontSize: 10,
  fontWeight: 800,
};

interface DisplayedMetricsProps {
  catalog: MetricCatalog | undefined;
  applied: Displayed;
  onChange: (value: Displayed) => void;
  surface: CreativesSurface;
}

export function DisplayedMetrics({
  catalog,
  applied,
  onChange,
  surface,
}: DisplayedMetricsProps): ReactNode {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [draft, setDraft] = useState<Displayed>(applied);
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState<string[]>([]);
  const [dragIndex, setDragIndex] = useState<number | null>(null);

  const catalogSize = useMemo(() => selectableNames(catalog).length, [catalog]);
  const groups = useMemo(() => groupMetrics(catalog, query), [catalog, query]);

  const labelOf = (name: string): string =>
    name === CREATIVE_ID_SORT_FIELD ? 'Creative' : (catalog?.[name]?.label ?? name);
  const atFloor = draft.metrics.length <= MIN_DISPLAYED_METRICS;
  const open = anchor !== null;

  const openPanel = (event: MouseEvent<HTMLElement>): void => {
    setDraft(applied);
    setQuery('');
    setExpanded([]);
    setAnchor(event.currentTarget);
  };

  const handleChipDrop =
    (targetIndex: number) =>
    (event: DragEvent<HTMLElement>): void => {
      event.preventDefault();
      if (dragIndex !== null) setDraft(moveMetric(draft, dragIndex, targetIndex));
      setDragIndex(null);
    };

  return (
    <>
      <Button
        variant="outlined"
        data-test="displayed-metrics-trigger"
        onClick={openPanel}
        disabled={catalogSize === 0}
        aria-expanded={open}
        sx={(theme) => ({
          ...FOOTER_BUTTON_SX,
          gap: 1,
          fontWeight: 700,
          borderColor: open ? theme.palette.primary.main : theme.palette.border.default,
          bgcolor: open ? theme.palette.info.bg : theme.palette.background.paper,
          color: open ? theme.palette.primary.main : theme.palette.text.primary,
          '&:hover': { borderColor: theme.palette.primary.main, color: theme.palette.primary.main },
          '&:disabled': { color: theme.palette.text.disabled },
        })}
      >
        Displayed Metrics
        <Box component="span" sx={{ ...COUNT_BADGE_SX, px: 1 }}>
          {applied.metrics.length}
        </Box>
        <ExpandMoreOutlined
          sx={(theme) => ({
            fontSize: 14,
            transform: open ? 'rotate(180deg)' : 'none',
            transition: theme.transitions.create('transform', {
              duration: theme.transitions.duration.shortest,
            }),
          })}
        />
      </Button>
      <Popover
        open={open}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: (theme) => ({
              mt: 2,
              width: 'min(392px, calc(100vw - 44px))',
              border: '1px solid',
              borderColor: theme.palette.border.default,
              borderRadius: `${theme.radius.xl}px`,
              boxShadow: shadows.dropdown,
            }),
          },
        }}
      >
        <Box sx={SECTION_SX}>
          <MenuSearchInput
            autoFocus
            placeholder={`Search ${catalogSize} metrics`}
            value={query}
            onChange={setQuery}
          />
        </Box>

        <Box
          sx={(theme) => ({
            ...SECTION_SX,
            borderTop: '1px solid',
            borderColor: theme.palette.border.default,
          })}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <Typography variant="overline" color="text.disabled">
              Sort table by
            </Typography>
            <Typography
              component="button"
              type="button"
              variant="overline"
              data-test="creatives-reset-sort"
              onClick={() => {
                AnalyticsService.track('Explore - Reset Sort');
                setDraft(setSortMetric(draft, catalog, preferredSortMetric(draft.metrics)));
              }}
              sx={LINK_BUTTON_SX}
            >
              Reset sort
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Select
              value={draft.sort}
              data-test="displayed-metrics-sort"
              onChange={(event) => setDraft(setSortField(draft, catalog, event.target.value))}
              sx={{ flex: 1, minWidth: 0, height: 30, fontWeight: 700 }}
            >
              <MenuItem value={CREATIVE_ID_SORT_FIELD}>Creative</MenuItem>
              {draft.metrics.map((name) => (
                <MenuItem key={name} value={name}>
                  {labelOf(name)}
                </MenuItem>
              ))}
            </Select>
            <Box
              sx={(theme) => ({
                display: 'inline-flex',
                flexShrink: 0,
                p: 0.5,
                border: '1px solid',
                borderColor: theme.palette.border.default,
                borderRadius: `${theme.radius.sm}px`,
                bgcolor: theme.palette.background.page,
              })}
            >
              {(
                [
                  ['desc', 'High to low', ArrowDownwardOutlined],
                  ['asc', 'Low to high', ArrowUpwardOutlined],
                ] as const
              ).map(([direction, label, DirectionIcon]) => (
                <Box
                  key={direction}
                  component="button"
                  type="button"
                  onClick={() => setDraft({ ...draft, direction })}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 1,
                    height: 24,
                    px: 2,
                    border: 0,
                    borderRadius: 0.5,
                    fontSize: 10,
                    fontWeight: 700,
                    whiteSpace: 'nowrap',
                    cursor: 'pointer',
                    ...(draft.direction === direction
                      ? {
                          bgcolor: 'background.paper',
                          color: 'primary.main',
                          boxShadow: shadows.chrome,
                        }
                      : { bgcolor: 'transparent', color: 'text.secondary' }),
                  }}
                >
                  <DirectionIcon sx={{ fontSize: 12 }} />
                  {label}
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        <Box
          sx={(theme) => ({
            ...SECTION_SX,
            borderTop: '1px solid',
            borderColor: theme.palette.border.default,
          })}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <Typography variant="overline" color="text.disabled">
              {`Selected · ${draft.metrics.length}`}
            </Typography>
            {atFloor ? null : (
              <Typography
                component="button"
                type="button"
                variant="overline"
                data-test="displayed-metrics-clear"
                title="Keep only the sort metric"
                onClick={() => setDraft(clearAllButSort(draft))}
                sx={LINK_BUTTON_SX}
              >
                Clear all but sort
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 1,
              maxHeight: 104,
              overflowY: 'auto',
            }}
          >
            {draft.metrics.map((name, index) => {
              const isSort = name === draft.sort;
              const SortFlagIcon = draft.direction === 'desc' ? ArrowDropDown : ArrowDropUp;
              return (
                <Box
                  key={name}
                  title={labelOf(name)}
                  draggable
                  onDragStart={() => setDragIndex(index)}
                  onDragOver={(event) => event.preventDefault()}
                  onDrop={handleChipDrop(index)}
                  onDragEnd={() => setDragIndex(null)}
                  sx={(theme) => ({
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    minWidth: 0,
                    height: 24,
                    pl: 1,
                    pr: 1,
                    borderRadius: `${theme.radius.sm}px`,
                    fontSize: 11,
                    fontWeight: 700,
                    bgcolor: isSort ? theme.palette.primary.main : theme.palette.info.bg,
                    color: isSort ? theme.palette.primary.contrastText : theme.palette.primary.main,
                    cursor: 'grab',
                    opacity: dragIndex === index ? 0.4 : 1,
                  })}
                >
                  <DragIndicatorOutlined sx={{ fontSize: 14, flexShrink: 0, opacity: 0.5 }} />
                  {isSort ? <SortFlagIcon sx={{ fontSize: 14, ml: -0.5, opacity: 0.9 }} /> : null}
                  <Box component="span" sx={ELLIPSIS_SX}>
                    {labelOf(name)}
                  </Box>
                  {atFloor ? null : (
                    <Box
                      component="button"
                      type="button"
                      aria-label={`Remove ${labelOf(name)}`}
                      onClick={() => setDraft(removeMetric(draft, catalog, name))}
                      sx={(theme) => ({
                        display: 'grid',
                        placeItems: 'center',
                        flexShrink: 0,
                        width: 15,
                        height: 15,
                        border: 0,
                        p: 0,
                        borderRadius: `${theme.radius.sm}px`,
                        bgcolor: 'transparent',
                        color: 'inherit',
                        opacity: 0.6,
                        cursor: 'pointer',
                        transition: theme.transitions.create('opacity', {
                          duration: theme.transitions.duration.shortest,
                        }),
                        '&:hover': { opacity: 1 },
                      })}
                    >
                      <CloseOutlined sx={{ fontSize: 10 }} />
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>

        <Box
          sx={(theme) => ({
            maxHeight: 284,
            overflowY: 'auto',
            px: 1,
            pb: 1,
            borderTop: '1px solid',
            borderColor: theme.palette.border.default,
          })}
        >
          {groups.length === 0 ? (
            <Typography color="text.disabled" sx={{ py: 3, px: 2, textAlign: 'center' }}>
              No metrics match “{query}”
            </Typography>
          ) : (
            groups.map((group) => {
              const isOpen = expanded.includes(group.category);
              const TwistyIcon = isOpen ? ArrowDropDown : ArrowRight;
              const selectedCount = group.metrics.filter((name) =>
                draft.metrics.includes(name),
              ).length;
              return (
                <Box key={group.category}>
                  <Box
                    component="button"
                    type="button"
                    aria-expanded={isOpen}
                    onClick={() =>
                      setExpanded(
                        isOpen
                          ? expanded.filter((current) => current !== group.category)
                          : [...expanded, group.category],
                      )
                    }
                    sx={(theme) => ({
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      width: '100%',
                      py: 1,
                      px: 2,
                      border: 0,
                      borderRadius: `${theme.radius.md}px`,
                      bgcolor: 'transparent',
                      fontWeight: 800,
                      textAlign: 'left',
                      cursor: 'pointer',
                      transition: theme.transitions.create('background-color', {
                        duration: theme.transitions.duration.shortest,
                      }),
                      '&:hover': { bgcolor: theme.palette.background.page },
                    })}
                  >
                    <TwistyIcon sx={{ flexShrink: 0, fontSize: 16, color: 'text.disabled' }} />
                    <Box component="span" sx={ELLIPSIS_SX}>
                      {categoryLabel(group.category)}
                    </Box>
                    {selectedCount > 0 ? (
                      <Box component="span" sx={COUNT_BADGE_SX}>
                        {selectedCount}
                      </Box>
                    ) : null}
                    <Box
                      component="span"
                      sx={{
                        width: 22,
                        flexShrink: 0,
                        textAlign: 'right',
                        fontSize: 10,
                        fontWeight: 700,
                        color: 'text.disabled',
                      }}
                    >
                      {group.metrics.length}
                    </Box>
                  </Box>
                  <Collapse in={isOpen} unmountOnExit>
                    {group.metrics.map((name) => {
                      const checked = draft.metrics.includes(name);
                      const locked = checked && atFloor;
                      return (
                        <Box
                          key={name}
                          component="button"
                          type="button"
                          disabled={locked}
                          aria-pressed={checked}
                          onClick={() => setDraft(toggleMetric(draft, catalog, name))}
                          sx={(theme) => ({
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            width: 'calc(100% - 15px)',
                            ml: 3,
                            py: 1,
                            px: 2,
                            border: 0,
                            borderRadius: `${theme.radius.md}px`,
                            bgcolor: 'transparent',
                            fontWeight: checked ? 700 : 400,
                            color: 'text.primary',
                            textAlign: 'left',
                            cursor: locked ? 'default' : 'pointer',
                            transition: theme.transitions.create('background-color', {
                              duration: theme.transitions.duration.shortest,
                            }),
                            '&:hover': {
                              bgcolor: locked ? 'transparent' : theme.palette.background.page,
                            },
                          })}
                        >
                          <FilterCheckbox checked={checked} />
                          <Box component="span" sx={ELLIPSIS_SX}>
                            {labelOf(name)}
                          </Box>
                        </Box>
                      );
                    })}
                  </Collapse>
                </Box>
              );
            })
          )}
        </Box>

        <Box
          sx={(theme) => ({
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 2,
            py: 2,
            px: 2,
            borderTop: '1px solid',
            borderColor: theme.palette.border.default,
            bgcolor: theme.palette.background.page,
          })}
        >
          <Button
            variant="outlined"
            onClick={() => setAnchor(null)}
            sx={(theme) => ({
              ...FOOTER_BUTTON_SX,
              color: 'text.secondary',
              borderColor: theme.palette.border.default,
              bgcolor: 'background.paper',
              '&:hover': { color: 'text.primary', borderColor: theme.palette.text.secondary },
            })}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            data-test="displayed-metrics-apply"
            onClick={() => {
              AnalyticsService.track('Metrics Picker - Apply', { surface, metrics: draft.metrics });
              onChange(draft);
              setAnchor(null);
            }}
            sx={FOOTER_BUTTON_SX}
          >
            Apply
          </Button>
        </Box>
      </Popover>
    </>
  );
}
